import { FC, ReactNode, useState } from 'react'
import { Button, Stack, Tooltip, Typography } from '@mui/material'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Activity, Camera, GBAddressCountry, Location, OutdoorEngagement, USAddressCountry } from '../../../../api/models'
import { formatPhoneNumberForDisplay } from '../../../location-management/LocationManagementPage/LocationSettingsTab/phoneNumberUtils'
import { getDisplayName } from '../../../../utils/cameraUtils'
import { governingSopDisplayName } from '../activityUtils'
import { UNKNOWN_TEXT } from './engagementDetailsUtils'
import { DEVICE_TOOLTIP_SLOT_PROPS } from './tooltipSlotProps'
import { buildOtherContacts, getCoverageLabel, getTimelineHandleTime } from './accountDetailsUtils'
import { getAgentAssignedTimestamp, SectionText } from './customerCareTimelineUtils'
import { ERROR_TEXT } from '../constants'

interface AccountDetailsProps {
  location: Location | undefined
  engagement: OutdoorEngagement | undefined
  cameras: Camera[] | undefined
  timelineFragments: SectionText[]
  activities: Activity[] | undefined
}

interface AccountDetailsTileProps {
  label: string
  value: string
  valueTooltip?: ReactNode
  'data-testid': string
}

// The last 8 characters of the camera ID are its serial number.
const SERIAL_NUMBER_LENGTH = 8

const buildDeviceTooltip = (camera: Camera): ReactNode => {
  const modelName = getDisplayName(camera.model)
  return (
    <Stack>
      {modelName && <span data-testid="account-details-device-model">Model: {modelName}</span>}
      <span data-testid="account-details-device-serial">Serial number: {camera.uuid.slice(-SERIAL_NUMBER_LENGTH)}</span>
    </Stack>
  )
}

const AccountOwnerHeader: FC = () => (
  <Typography sx={{ color: 'var(--text-secondary)', fontSize: 'var(--size-2xs)', textTransform: 'uppercase' }}>Account Owner</Typography>
)

const AccountDetailsTile: FC<AccountDetailsTileProps> = ({ label, value, valueTooltip, 'data-testid': dataTestId }) => {
  // The tooltip is attached to just the value text (not the full-width row) and underlined to signal it's hoverable.
  const valueText = valueTooltip ? (
    <Tooltip title={valueTooltip} placement="top-start" arrow slotProps={DEVICE_TOOLTIP_SLOT_PROPS}>
      <span style={{ textDecoration: 'underline' }}>{value}</span>
    </Tooltip>
  ) : (
    value
  )

  return (
    <Stack
      sx={{
        flex: '1 0 0',
        gap: 'var(--spacing-3xs)',
        padding: 'var(--spacing-s)',
        backgroundColor: 'var(--bg-page)',
        borderRadius: 'var(--border-radius-l)'
      }}
      data-testid={dataTestId}
    >
      <Typography sx={{ color: 'var(--text-secondary)', fontSize: 'var(--size-2xs)', lineHeight: 'var(--line-height-xs)' }}>{label}</Typography>
      <Typography sx={{ color: 'var(--text-primary)', fontSize: 'var(--size-s)', fontWeight: 500, lineHeight: 'var(--line-height-s)' }}>
        {valueText}
      </Typography>
    </Stack>
  )
}

export const AccountDetails: FC<AccountDetailsProps> = ({ location, engagement, cameras, timelineFragments, activities }) => {
  const [showAllUsers, setShowAllUsers] = useState(false)

  if (!location) {
    return (
      <Stack
        sx={{
          gap: 'var(--spacing-m)',
          padding: 'var(--spacing-m)',
          width: '100%',
          backgroundColor: 'var(--bg-surface)',
          border: '1px solid var(--border-medium)',
          borderRadius: 'var(--border-radius-xl)',
          boxShadow: '0px 1px 2px 0px rgba(28,25,23,0.05)'
        }}
        data-testid="account-details"
      >
        <AccountOwnerHeader />
        <Typography data-testid="account-details-error" sx={{ color: 'var(--text-secondary)', fontSize: 'var(--size-xs)', textAlign: 'center' }}>
          {ERROR_TEXT}
        </Typography>
      </Stack>
    )
  }

  const phoneCountry = (location.address?.country as USAddressCountry | GBAddressCountry | undefined) ?? USAddressCountry.US
  const owner = location.contacts?.primaryContacts[0]
  const ownerName = owner?.name ? `${owner.name.firstName} ${owner.name.lastName}`.trim() : ''
  const ownerPhone = owner?.phone.number && formatPhoneNumberForDisplay(owner.phone.number, phoneCountry)
  const contactLineParts = [location.address?.street1, ownerPhone, location.user.emailAddress].filter(part => !!part)
  const otherContacts = buildOtherContacts(location, phoneCountry)
  const triggeringCamera = engagement && cameras ? cameras.find(camera => camera.uuid === engagement.triggeringDevice.id) : undefined
  const deviceName = engagement && cameras ? triggeringCamera?.name : UNKNOWN_TEXT
  // From agent assignment to the end of the Event Timeline. With no assignment (e.g. cleared by AI) or no timeline rows (e.g. the event dropped), there's no handle time.
  const handleTimeLabel = getTimelineHandleTime(timelineFragments, activities && getAgentAssignedTimestamp(activities)) ?? UNKNOWN_TEXT

  const toggleShowAllUsers = () => setShowAllUsers(!showAllUsers)

  return (
    <Stack
      sx={{
        gap: 'var(--spacing-m)',
        padding: 'var(--spacing-m)',
        width: '100%',
        backgroundColor: 'var(--bg-surface)',
        border: '1px solid var(--border-medium)',
        borderRadius: 'var(--border-radius-xl)',
        boxShadow: '0px 1px 2px 0px rgba(28,25,23,0.05)'
      }}
      data-testid="account-details"
    >
      <Stack direction="row" sx={{ gap: 'var(--spacing-m)', alignItems: 'center' }}>
        <Stack sx={{ flex: '1 0 0', gap: 'var(--spacing-3xs)' }}>
          <AccountOwnerHeader />
          <Typography data-testid="account-details-owner-name" sx={{ color: 'var(--text-primary)', fontSize: 'var(--size-xl)', fontWeight: 500 }}>
            {ownerName}
          </Typography>
          <Typography data-testid="account-details-contact-line" sx={{ color: 'var(--text-secondary)', fontSize: 'var(--size-2xs)' }}>
            {contactLineParts.join(' · ')}
          </Typography>
        </Stack>
        {otherContacts.length > 0 && (
          <Button
            onClick={toggleShowAllUsers}
            data-testid="account-details-toggle-contacts"
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 'var(--spacing-2xs)',
              alignItems: 'center',
              border: 'none',
              padding: 'var(--spacing-3xs) var(--spacing-s)',
              backgroundColor: 'var(--bg-page)',
              borderRadius: 'var(--border-radius-round)',
              textTransform: 'none'
            }}
          >
            <Typography sx={{ color: 'var(--text-secondary)', fontSize: 'var(--size-2xs)' }}>
              {showAllUsers ? 'Hide all users' : 'Show all users'}
            </Typography>
            {showAllUsers ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </Button>
        )}
      </Stack>
      {showAllUsers && (
        <Stack
          data-testid="account-details-contacts-list"
          sx={{
            border: '1px solid var(--border-card, #E7E5E4)',
            borderRadius: 'var(--border-radius-l)',
            boxShadow: '0px 1px 2px 0px rgba(28,25,23,0.05)'
          }}
        >
          {otherContacts.map(contact => (
            <Stack
              key={contact.key}
              data-testid="account-details-contact-row"
              sx={{
                gap: 'var(--spacing-3xs)',
                padding: 'var(--spacing-2xs) var(--spacing-xs)',
                borderRadius: 'var(--border-radius-l)',
                borderBottom: '1px solid var(--border-low)'
              }}
            >
              <Typography sx={{ color: 'var(--text-primary)', fontSize: 'var(--size-xs)', fontWeight: 500 }}>{contact.name}</Typography>
              <Typography sx={{ color: 'var(--text-secondary)', fontSize: 'var(--size-2xs)' }}>
                {contact.role} · {contact.phone}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}
      <Stack direction="row" sx={{ gap: 'var(--spacing-s)', borderTop: '1px solid var(--border-low)', paddingTop: 'var(--spacing-m)' }}>
        <AccountDetailsTile
          label="Agent Protocol"
          value={engagement ? governingSopDisplayName(engagement.configuration?.isTwoWayAudioEscalationOnly) : UNKNOWN_TEXT}
          data-testid="account-details-tile-voice-policy"
        />
        <AccountDetailsTile
          label="Plan & coverage"
          value={location ? `${location.plan.name} · ${getCoverageLabel(location)}` : UNKNOWN_TEXT}
          data-testid="account-details-tile-plan-coverage"
        />
        {deviceName && (
          <AccountDetailsTile
            label="Device"
            value={deviceName}
            valueTooltip={triggeringCamera && buildDeviceTooltip(triggeringCamera)}
            data-testid="account-details-tile-device"
          />
        )}
        <AccountDetailsTile label="Handle time" value={handleTimeLabel} data-testid="account-details-tile-handle-time" />
      </Stack>
    </Stack>
  )
}

AccountDetails.displayName = 'AccountDetails'
